/**
 * A custom element that renders a styleable listbox UI on top of a visually
 * hidden native <select>. The select stays in the DOM and keeps receiving
 * value changes, so every existing variant-picker behaviour (price updates,
 * availability, combined-listing product morphing) that listens for a
 * bubbled 'change' event on the select continues to work unmodified.
 */
class CustomSelectDropdown extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.select = this.querySelector('select');
    this.trigger = this.querySelector('.variant-option__select-trigger');
    this.triggerText = this.querySelector('.variant-option__select-trigger-text');
    this.listbox = this.querySelector('.variant-option__select-listbox');

    if (!this.select || !this.trigger || !this.listbox) return;

    this.initialized = true;

    this.buildOptions();
    this.syncTriggerText();

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.listbox.addEventListener('click', this.onListboxClick);
    this.listbox.addEventListener('keydown', this.onListboxKeydown);
    this.select.addEventListener('change', this.onSelectChange);
    this.ownerDocument.addEventListener('click', this.onDocumentClick);
    this.ownerDocument.addEventListener('keydown', this.onDocumentKeydown);

    // The variant picker re-renders by fetching fresh section HTML and morphing
    // the WHOLE picker with it (see variant-picker.js / morph.js) whenever ANY
    // option anywhere in the picker changes -- not just the one that changed.
    // The server HTML for this listbox is always an empty <ul> (it's only ever
    // populated client-side), so every single one of those morphs wipes the
    // <li> items here out -- including on a dropdown the shopper never touched
    // (e.g. changing Color morphs Size's listbox empty too, since they share
    // one variant-picker element). Watch the listbox itself for that wipe and
    // rebuild from the (by-then up to date) select. takeRecords() after our own
    // rebuild discards the mutation it just caused, so this can't self-trigger.
    this.listboxObserver = new MutationObserver(() => {
      this.buildOptions();
      this.syncTriggerText();
    });
    this.listboxObserver.observe(this.listbox, { childList: true });
  }

  disconnectedCallback() {
    this.ownerDocument.removeEventListener('click', this.onDocumentClick);
    this.ownerDocument.removeEventListener('keydown', this.onDocumentKeydown);
    this.listboxObserver?.disconnect();
  }

  buildOptions() {
    const options = Array.from(this.select.options);

    this.listbox.replaceChildren(
      ...options.map((option) => {
        const item = document.createElement('li');

        item.setAttribute('role', 'option');
        item.setAttribute('data-value', option.value);
        item.setAttribute('aria-selected', option.selected ? 'true' : 'false');
        item.textContent = option.textContent.trim();

        if (option.disabled) item.setAttribute('aria-disabled', 'true');

        return item;
      })
    );

    this.listboxObserver?.takeRecords();
  }

  syncTriggerText() {
    const selectedOption = this.select.options[this.select.selectedIndex];

    if (this.triggerText && selectedOption) {
      this.triggerText.textContent = selectedOption.textContent.trim();
    }

    for (const item of this.listbox.children) {
      item.setAttribute('aria-selected', item.dataset.value === this.select.value ? 'true' : 'false');
    }
  }

  open = () => {
    if (this.isOpen) return;

    this.isOpen = true;
    this.listbox.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.positionListbox();

    const items = Array.from(this.listbox.children);
    const selectedIndex = items.findIndex((item) => item.getAttribute('aria-selected') === 'true');

    this.focusItem(items, selectedIndex === -1 ? 0 : selectedIndex);
    this.listbox.focus();
  };

  // The listbox opens downward by default. When the trigger sits low on the
  // page (e.g. a "Size" option right above the Add to Cart button), there
  // isn't always enough room below it before the viewport edge, which makes
  // the panel look cut off. Flip it to open upward when that's the roomier
  // side.
  positionListbox() {
    const triggerRect = this.trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const flipUp = spaceBelow < this.listbox.scrollHeight && spaceAbove > spaceBelow;

    this.listbox.classList.toggle('variant-option__select-listbox--flip-up', flipUp);
  }

  close = () => {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.listbox.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');

    for (const item of this.listbox.children) {
      item.classList.remove('variant-option__select-listbox-item--highlighted');
    }
  };

  toggle = () => {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  selectValue(value) {
    if (this.select.value === value) {
      this.close();
      this.trigger.focus();
      return;
    }

    this.select.value = value;
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.close();
    this.trigger.focus();
  }

  onTriggerClick = () => {
    this.toggle();
  };

  onListboxClick = (event) => {
    const item = event.target.closest('[role="option"]');

    if (!item || item.getAttribute('aria-disabled') === 'true') return;

    this.selectValue(item.dataset.value);
  };

  onListboxKeydown = (event) => {
    const items = Array.from(this.listbox.children);
    let currentIndex = items.findIndex((item) =>
      item.classList.contains('variant-option__select-listbox-item--highlighted')
    );
    if (currentIndex === -1) {
      currentIndex = items.findIndex((item) => item.getAttribute('aria-selected') === 'true');
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusItem(items, Math.min(currentIndex + 1, items.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusItem(items, Math.max(currentIndex - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        this.focusItem(items, 0);
        break;
      case 'End':
        event.preventDefault();
        this.focusItem(items, items.length - 1);
        break;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const highlighted = this.listbox.querySelector('.variant-option__select-listbox-item--highlighted');
        if (highlighted && highlighted.getAttribute('aria-disabled') !== 'true') {
          this.selectValue(highlighted.dataset.value);
        }
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.close();
        this.trigger.focus();
        break;
      case 'Tab':
        this.close();
        break;
    }
  };

  focusItem(items, index) {
    for (const item of items) item.classList.remove('variant-option__select-listbox-item--highlighted');

    const item = items[index];
    if (!item) return;

    item.classList.add('variant-option__select-listbox-item--highlighted');
    item.scrollIntoView({ block: 'nearest' });
  }

  onSelectChange = () => {
    this.syncTriggerText();
  };

  onDocumentClick = (event) => {
    if (this.isOpen && !this.contains(event.target)) this.close();
  };

  onDocumentKeydown = (event) => {
    if (this.isOpen && event.key === 'Escape') this.close();
  };
}

if (!customElements.get('custom-select-dropdown')) {
  customElements.define('custom-select-dropdown', CustomSelectDropdown);
}
